/**
 * TanStack Query hook for fetching batch job state.
 *
 * Fetches GET /api/v2/batch/{jobId} with conditional polling:
 *  - Polls every 5 s while job is running (stage1_running or stage2_running)
 *  - Stops polling once complete, cancelled, or failed
 *
 * Usage:
 *   const { data, isLoading, error, results, jobStatus } = useBatchJob(jobId);
 */

import { useQuery } from '@tanstack/react-query';
import type { BatchJobState, BatchJobStatus } from '../types/batch';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const RUNNING_STATUSES: BatchJobStatus[] = ['stage1_running', 'stage2_running'];

async function fetchBatchJob(jobId: string): Promise<BatchJobState> {
  const response = await fetch(`${API_BASE}/api/v2/batch/${jobId}`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  return response.json();
}

export function useBatchJob(jobId: string | undefined) {
  const query = useQuery<BatchJobState, Error>({
    queryKey: ['batchJob', jobId],
    queryFn: () => fetchBatchJob(jobId!),
    enabled: !!jobId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchInterval: (queryState) => {
      const status = queryState.state.data?.status;
      if (status && RUNNING_STATUSES.includes(status)) {
        return 5_000; // Poll every 5 s while running
      }
      return false; // No polling when done / cancelled / failed
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    // Derived helpers
    results: query.data?.results ?? [],
    jobStatus: query.data?.status,
  };
}
